/**
 * Frontend feature flags.
 *
 * Each flag is a build-time toggle backed by a `VITE_FF_*` env var. Vite
 * replaces `import.meta.env.VITE_*` at build time, so consumers can do
 *
 *     if (isFeatureEnabled('collab')) { … }
 *
 * with no runtime cost. Tests can override via `setFeatureFlagOverrides()`
 * — overrides win over env, env wins over the safe default.
 *
 * Safe defaults are OFF. Any new IA surface gated by a flag MUST render a
 * production-ready gated placeholder when its flag is off (see
 * `<GatedFeaturePlaceholder>` in `src/components/GatedFeaturePlaceholder.tsx`).
 *
 * Adding a flag:
 *  1. Append the key + default to `FLAG_DEFINITIONS` below.
 *  2. Mirror the env-var line in `frontend/.env.example`.
 *  3. Document it in the README "Feature flags" section.
 */

export type FeatureFlag =
  | 'collab'
  | 'ai_editing'
  | 'uploads_previews'
  | 'mobile_redesign'

interface FlagDefinition {
  /** Env var name. Build-time replaced by Vite. */
  envVar: `VITE_FF_${string}`
  /** Safe default when no env var is set. Always defaults to `false`. */
  default: boolean
  /** Short human-readable summary for docs / UI. */
  description: string
}

const FLAG_DEFINITIONS: Record<FeatureFlag, FlagDefinition> = {
  collab: {
    envVar: 'VITE_FF_COLLAB',
    default: false,
    description:
      'Preview collaboration surfaces that are not part of the core projects browser.',
  },
  ai_editing: {
    envVar: 'VITE_FF_AI_EDITING',
    default: false,
    description: 'AI-assisted inline editing on documents.',
  },
  uploads_previews: {
    envVar: 'VITE_FF_UPLOADS_PREVIEWS',
    default: false,
    description: 'Dedicated uploads surface with rich previews and re-use.',
  },
  mobile_redesign: {
    envVar: 'VITE_FF_MOBILE_REDESIGN',
    default: false,
    description: 'Redesigned mobile chrome (bottom-tabs + compact upload).',
  },
}

function parseFlagValue(raw: string | undefined): boolean | null {
  if (raw === undefined || raw === '') return null
  const lowered = raw.toLowerCase()
  if (lowered === '1' || lowered === 'true' || lowered === 'yes' || lowered === 'on') {
    return true
  }
  if (lowered === '0' || lowered === 'false' || lowered === 'no' || lowered === 'off') {
    return false
  }
  return null
}

const overrides: Partial<Record<FeatureFlag, boolean>> = {}

/**
 * Test-only: force a specific value for one or more flags. Always pair with
 * `clearFeatureFlagOverrides()` in an `afterEach` so other tests don't leak.
 */
export function setFeatureFlagOverrides(
  next: Partial<Record<FeatureFlag, boolean>>,
): void {
  Object.assign(overrides, next)
}

/** Test-only: drop every override set by `setFeatureFlagOverrides`. */
export function clearFeatureFlagOverrides(): void {
  for (const key of Object.keys(overrides) as FeatureFlag[]) {
    delete overrides[key]
  }
}

function readEnv(envVar: `VITE_FF_${string}`): string | undefined {
  // `ImportMetaEnv` declares an indexed signature for `VITE_FF_*` keys (see
  // `src/vite-env.d.ts`), so this stays type-safe with no `as unknown` cast.
  return import.meta.env[envVar]
}

/** Resolve the current value of `flag`. Overrides > env > safe default. */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  if (Object.prototype.hasOwnProperty.call(overrides, flag)) {
    return overrides[flag]!
  }
  const def = FLAG_DEFINITIONS[flag]
  const fromEnv = parseFlagValue(readEnv(def.envVar))
  if (fromEnv !== null) return fromEnv
  return def.default
}

/** Snapshot of every flag's current resolved value. */
export function getFeatureFlags(): Record<FeatureFlag, boolean> {
  const out = {} as Record<FeatureFlag, boolean>
  for (const key of Object.keys(FLAG_DEFINITIONS) as FeatureFlag[]) {
    out[key] = isFeatureEnabled(key)
  }
  return out
}

/** Metadata for docs / UI surfaces (e.g. an admin-only flag inspector). */
export function getFeatureFlagDefinitions(): readonly {
  key: FeatureFlag
  envVar: string
  description: string
  default: boolean
}[] {
  return (Object.keys(FLAG_DEFINITIONS) as FeatureFlag[]).map((key) => ({
    key,
    envVar: FLAG_DEFINITIONS[key].envVar,
    description: FLAG_DEFINITIONS[key].description,
    default: FLAG_DEFINITIONS[key].default,
  }))
}
